import { Suspense, type ReactNode } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
// Self-healing dynamic-import wrapper: retries transient chunk-fetch failures
// and reloads ONCE (sessionStorage-guarded) on a stale-after-deploy chunk 404,
// so the "Failed to fetch dynamically imported module" (SYS_001) error class
// self-heals instead of white-screening. Aliased to `lazy` so the route
// definitions below read identically to bare React.lazy.
import { lazyWithRetry as lazy } from './utils/lazyWithRetry';
import { PreviewBanner } from './components/PreviewBanner';

// Auth Pages (Public) - loaded eagerly since they're entry points
import { LoginPage } from './pages/auth/LoginPage';
import { ChangePasswordPage } from './pages/auth/ChangePasswordPage';
import { ForgotPasswordPage } from './pages/auth/ForgotPasswordPage';
import { UnauthorizedPage } from './pages/auth/UnauthorizedPage';

// Protected Pages - lazy loaded for code splitting
const DashboardPage = lazy(() => import('./pages/DashboardPage').then(m => ({ default: m.DashboardPage })));
const HomePage = lazy(() => import('./pages/HomePage'));
const ManageUsersPage = lazy(() => import('./pages/ManageUsersPage'));
const DirectoryPage = lazy(() => import('./pages/directory/DirectoryPage'));
const MyProfilePage = lazy(() => import('./pages/my-profile/MyProfilePage'));
const PropertiesPage = lazy(() => import('./pages/PropertiesPage'));
const PropertyDetailPage = lazy(() => import('./pages/PropertyDetailPage'));
const TransactionDetailPage = lazy(() => import('./pages/TransactionDetailPage'));
const PropertyFormPage = lazy(() => import('./pages/PropertyFormPage'));
const IngestionPage = lazy(() => import('./pages/IngestionPage'));
const IngestionReviewPage = lazy(() => import('./pages/IngestionReviewPage'));
const IngestionJobDetailPage = lazy(() => import('./pages/IngestionJobDetailPage'));
const IngestionPhotosPage = lazy(() => import('./pages/IngestionPhotosPage'));
// BrokerPortalPage was removed 2026-06-12 — the dashboard now renders a
// Broker-specific layout when the logged-in user's role is "Broker".
// See DashboardPage.tsx + components/dashboard/DashboardRenderer.tsx for the replacement.
const LoadingTestPage = lazy(() => import('./pages/LoadingTestPage'));
const CompIntelligencePage = lazy(() => import('./pages/CompIntelligencePage'));
const OpsDashboardPage = lazy(() => import('./pages/OpsDashboardPage'));
const VerificationPage = lazy(() => import('./pages/VerificationPage'));
const NumberFormattingDemo = lazy(() => import('./pages/NumberFormattingDemo'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const PermissionMatrixSettings = lazy(() => import('./pages/PermissionMatrixSettings'));
const DashboardDesignerPage = lazy(() => import('./pages/DashboardDesignerPage'));
const SubmarketSettingsPage = lazy(() => import('./pages/SubmarketSettingsPage'));
const DataMaintenanceSettingsPage = lazy(() => import('./pages/DataMaintenanceSettingsPage'));
const AppSettingsPage = lazy(() => import('./pages/app-settings/AppSettingsPage'));
const LeaseTermsSettingsPage = lazy(() => import('./pages/lease-terms-settings/LeaseTermsSettingsPage'));
const FormTypesSettingsPage = lazy(() => import('./pages/form-types-settings/FormTypesSettingsPage'));
const AiPage = lazy(() => import('./pages/ai/AiPage'));
const AiLabRunPage = lazy(() => import('./pages/ai-lab/AiLabRunPage'));
const AiLabVariantPage = lazy(() => import('./pages/ai-lab/AiLabVariantPage'));
const DataCurationPage = lazy(() => import('./pages/data-curation/DataCurationPage'));
const SourcesSettingsPage = lazy(() => import('./pages/SourcesSettingsPage'));
const AdminInfoSettingsPage = lazy(() => import('./pages/AdminInfoSettingsPage'));
const CalculatedFieldsSettingsPage = lazy(
  () => import('./pages/CalculatedFieldsSettingsPage'),
);
const DealsPage = lazy(() => import('./pages/DealsPage'));
const DocumentsPage = lazy(() => import('./pages/DocumentsPage'));
// Release Notes is an internal-only devtools surface. NEVER for the
// client (decision 2026-07-20): present in every build (local, preview,
// prod) but gated to whitelisted super-admin emails — the sidebar entry,
// this route (redirect below), and the backend /api/release-notes*
// routes all read the same whitelist. See
// @aiga/app-config/security.
const ReleaseNotesPage = lazy(() => import('./pages/ReleaseNotesPage'));
// Internal Developer surface for reported errors. Always present in
// the bundle (preview + prod) because the entire point is to surface
// runtime errors on remote sites — but gated to whitelisted super-admin
// emails by both the page itself and the backend route. See
// @aiga/app-config/security.
const DeveloperErrorReportsPage = lazy(() => import('./pages/DeveloperErrorReportsPage'));
// Listings (L1-3, L1-4) — listing projects, My work and the agreement activity on
// the real Listings API, in the client's mock-up look (docs/listings/README.md).
const ListingsLayout = lazy(() => import('./pages/listings/ListingsLayout'));
const ListingProjectsPage = lazy(() => import('./pages/listings/ListingProjectsPage'));
const ListingsDashboardPage = lazy(() => import('./pages/listings/work/ListingsDashboardPage'));
const ListingProjectPage = lazy(() => import('./pages/listings/ListingProjectPage'));
const NewListingPage = lazy(() => import('./pages/listings/NewListingPage'));
const ListingActivityPage = lazy(() => import('./pages/listings/ListingActivityPage'));
const TeamWorkloadPage = lazy(() => import('./pages/listings/workload/TeamWorkloadPage'));
const SignRequestsPage = lazy(() => import('./pages/listings/signs/SignRequestsPage'));
const ListingTemplatesSettingsPage = lazy(() => import('./pages/listing-templates/ListingTemplatesSettingsPage'));
// Property management (preview) — the Lease intelligence mock-up, admin-only, sample data.
const PropertyManagementSection = lazy(() => import('./pages/property-management/PropertyManagementSection'));
const ManagedPropertiesPage = lazy(() => import('./pages/property-management/ManagedPropertiesPage'));
const PropertyManagementLayout = lazy(() => import('./pages/property-management/PropertyManagementLayout'));
const LeaseIntelligencePage = lazy(() => import('./pages/property-management/LeaseIntelligencePage'));
const LeaseExpirationsPage = lazy(() => import('./pages/property-management/LeaseExpirationsPage'));
// Unified client request-tracker (F3). Tier-gated (dev|client) via
// @aiga/app-config/security — a separate surface from
// the internal-only Release Notes devtools page.
const RequestTrackerPage = lazy(() => import('./pages/RequestTrackerPage'));
// Forms (forms cards 10–11): the inbox + My forms, one submission page and the start page for every form type; the API decides access.
const FormsInboxPage = lazy(() => import('./pages/forms/FormsInboxPage'));
const FormSubmissionPage = lazy(() => import('./pages/forms/FormSubmissionPage'));
const NewFormPage = lazy(() => import('./pages/forms/NewFormPage'));
// Forms+ 08: the directory of forms the caller may start.
const FormsDirectoryPage = lazy(() => import('./pages/forms/FormsDirectoryPage'));

// Comp workflow (new)
const NewCompRequestPage = lazy(() => import('./pages/comps/NewCompRequestPage'));
// TODO: re-enable Self Serve Search — disabled 2026-05-15
// const CompSearchPage = lazy(() => import('./pages/comps/CompSearchPage'));
const MyRequestsPage = lazy(() => import('./pages/comps/MyRequestsPage'));
const AllReportsPage = lazy(() => import('./pages/comps/AllReportsPage'));
const CompRequestDetailPage = lazy(() => import('./pages/comps/CompRequestDetailPage'));
const CompReportPage = lazy(() => import('./pages/comps/CompReportPage'));
const CompReportPreviewPage = lazy(() => import('./pages/comps/CompReportPreviewPage'));
const CompReportTemplatesPage = lazy(() => import('./pages/comps/CompReportTemplatesPage'));
// F-TT Phase 1 (2026-06-19): broker-side submit form + research-side queue.
const SubmitToResearchPage = lazy(() => import('./pages/SubmitToResearchPage'));
const BrokerSubmissionsQueuePage = lazy(() => import('./pages/reviews/BrokerSubmissionsQueuePage'));

// Review & Reporting workflow
const ReviewQueuePage = lazy(() => import('./pages/reviews/ReviewQueuePage'));
const ReviewDetailPage = lazy(() => import('./pages/reviews/ReviewDetailPage'));
const SubmissionReviewQueuePage = lazy(
  () => import('./pages/reviews/SubmissionReviewQueuePage'),
);

// Components
import Layout from './components/Layout';
import { SessionExpirationHandler } from './components/auth';
import { FeatureGate } from './components/rbac/FeatureGate';
import { SettingsDenied } from './components/settings/SettingsDenied';
import { useCanOpenSettingsHub } from './components/settings/useVisibleSettingsEntries';
import { DEVTOOLS_CRUMB } from './components/settings/SettingsPageShell';
import { AI_LAB_CRUMBS } from './components/settings/settingsTabs';
import { DataCurationRedirect } from './pages/data-curation/DataCurationRedirect';
import { AiAreaRoute } from './components/ai-lab/AiAreaRoute';
import { useCanEditListingTemplates, useCanSeeListings, useCanSeeTeamWorkload, useCanWorkListings } from './components/listings/useListingsAccess';
import { useCanSeePropertyManagement, useCanWorkPropertyManagement } from './components/property-management/usePropertyManagementAccess';
import { useFeatureAccess } from './hooks/useFeatureAccess';
import { useCanSeeAppSettings } from './components/settings-registry/useCanSeeAppSettings';
import { useCanOpenDataMaintenance } from './components/settings/useDataMaintenanceAccess';
import { useCanManageForms } from './components/forms/useFormsAccess';
import { useAuth } from './contexts/AuthContext';
import { useIsSuperAdmin } from './hooks/useIsSuperAdmin';
import { useIsAdminOrAig } from './hooks/useIsAdminOrAig';
import { useRequestTrackerTier } from './hooks/useRequestTrackerTier';

/**
 * Whitelist gate for internal devtools routes (release notes). Renders
 * children only for whitelisted super-admin emails; everyone else is
 * redirected to the dashboard. The backend enforces the same whitelist
 * on /api/release-notes*, so this redirect is UX, not security.
 */
function SuperAdminRoute({ children }: { children: ReactNode }) {
  const isSuperAdmin = useIsSuperAdmin();
  if (!isSuperAdmin) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

/**
 * Admin Info and Calculated Fields under /devtools (M286 idea 18): an admin role or AIG staff (useIsAdminOrAig), so a
 * client Admin keeps them; everyone else sees SettingsDenied under Developer tools, in place.
 */
function AdminOrAigRoute({ title, children }: { title: string; children: ReactNode }) {
  const { isInitialized } = useAuth();
  const canSee = useIsAdminOrAig();
  if (!isInitialized) return null;
  if (!canSee) return <SettingsDenied title={title} permission="an admin role" root={DEVTOOLS_CRUMB} />;
  return <>{children}</>;
}

/**
 * Tier gate for the unified request-tracker route. Renders children only for
 * users whose email resolves to a request-tracker tier (dev|client) in
 * @aiga/app-config/security; tier===null redirects to the
 * dashboard. The backend enforces the same whitelist on /api/release-notes*,
 * so this redirect is UX, not security. NOT gated on isSuperAdmin — a future
 * client tier must be able to reach it.
 */
function RequestTrackerRoute({ children }: { children: ReactNode }) {
  const tier = useRequestTrackerTier();
  if (tier === null) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

/**
 * Listings gate (L1-3, L2-1): feature:listings, listings_work or an admin
 * (useCanSeeListings); everyone else → /unauthorized. `work` screens (My work,
 * New listing) also need listings_work: a Broker goes back to its listing
 * agreements; `workload` (Team workload, M22b) needs an admin or listings_work +
 * widget:team_workload (useCanSeeTeamWorkload), everyone else goes back to the
 * projects list. The Listings API decides every call itself, so this is UX, not
 * security.
 */
function ListingsRoute({ children, work = false, workload = false }: { children: ReactNode; work?: boolean; workload?: boolean }) {
  const { isInitialized } = useAuth();
  const canSee = useCanSeeListings();
  const canWork = useCanWorkListings();
  const canSeeWorkload = useCanSeeTeamWorkload();
  if (!isInitialized) return null;
  if (!canSee) return <Navigate to="/unauthorized" replace />;
  if ((work && !canWork) || (workload && !canSeeWorkload)) return <Navigate to="/listings" replace />;
  return <>{children}</>;
}

/**
 * Task templates settings (L1-4): feature:listings_templates or an admin, as the
 * template writes on the server; everyone else sees SettingsDenied (M286), like the other settings routes.
 */
function ListingTemplatesRoute({ children }: { children: ReactNode }) {
  const { isInitialized } = useAuth();
  const canEdit = useCanEditListingTemplates();
  if (!isInitialized) return null;
  if (!canEdit) return <SettingsDenied title="Listing templates" permission="listings_templates" />;
  return <>{children}</>;
}

/**
 * Property management gate (PM-1): feature:property_management (or _all / _work) or an admin
 * (useCanSeePropertyManagement); everyone else → /unauthorized. The API decides every call itself.
 */
function PropertyManagementRoute({ children }: { children: ReactNode }) {
  const { isInitialized } = useAuth();
  const canSee = useCanSeePropertyManagement();
  if (!isInitialized) return null;
  if (!canSee) return <Navigate to="/unauthorized" replace />;
  return <>{children}</>;
}

/**
 * Lease terms settings (TERM-EDITOR): feature:property_management_work or an admin
 * (useCanWorkPropertyManagement), as the server's /api/property-management/lease-term-kinds; else SettingsDenied.
 */
function LeaseTermsRoute({ children }: { children: ReactNode }) {
  const { isInitialized } = useAuth();
  const canWork = useCanWorkPropertyManagement();
  if (!isInitialized) return null;
  if (!canWork) return <SettingsDenied title="Lease terms" permission="property_management_work" />;
  return <>{children}</>;
}

/** Settings → Forms (forms card 13): an admin, or MANAGE on a form type (useCanManageForms); the API decides every call. */
function FormsSettingsRoute({ children }: { children: ReactNode }) {
  const { isInitialized } = useAuth();
  const { canManage, isLoading } = useCanManageForms();
  if (!isInitialized || isLoading) return null;
  if (!canManage) return <SettingsDenied title="Forms" permission="an admin role, or Manage on a form type" />;
  return <>{children}</>;
}

/** App settings (CONFIG CF-10): an admin, or a holder of a settings group's key, as GET /api/settings. */
function AppSettingsRoute({ children }: { children: ReactNode }) {
  const { isInitialized } = useAuth();
  const canSee = useCanSeeAppSettings();
  if (!isInitialized) return null;
  if (!canSee) return <SettingsDenied title="App settings" permission="an admin role, or the permission that edits one of its settings groups" />;
  return <>{children}</>;
}

/**
 * The Settings hub: feature:settings, or anyone who may open Data Maintenance (Research's Land area mismatch list,
 * M286 idea 15; useCanOpenSettingsHub); everyone else → /dashboard, as the FeatureGate it replaces.
 */
function SettingsHubRoute({ children }: { children: ReactNode }) {
  const { isInitialized } = useAuth();
  const canOpen = useCanOpenSettingsHub();
  if (!isInitialized) return null;
  if (!canOpen) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

/**
 * Data Maintenance: feature:settings_data_maintenance, or Research / an admin for the Land area
 * mismatch list (LAND-MISMATCH-b); everyone else sees SettingsDenied (M286).
 */
function DataMaintenanceRoute({ children }: { children: ReactNode }) {
  const { isInitialized } = useAuth();
  const canOpen = useCanOpenDataMaintenance();
  if (!isInitialized) return null;
  if (!canOpen) return <SettingsDenied title="Data Maintenance" permission="settings_data_maintenance" />;
  return <>{children}</>;
}

/**
 * Dashboard: feature:dashboard, as GET /api/dashboard/summary on the server (module view gate). A role
 * without it goes to the first module it can open — Properties, then Listings, then Property
 * management — else /unauthorized. Every other gate sends a refused user to /dashboard, so this never
 * redirects back here.
 */
function DashboardRoute({ children }: { children: ReactNode }) {
  const { isInitialized } = useAuth();
  const canSee = useFeatureAccess('dashboard');
  const canSeeProperties = useFeatureAccess('properties');
  const canSeeListings = useCanSeeListings();
  const canSeePropertyManagement = useCanSeePropertyManagement();
  if (!isInitialized) return null;
  if (canSee) return <>{children}</>;
  const home = canSeeProperties ? '/properties' : canSeeListings ? '/listings' : canSeePropertyManagement ? '/property-management' : '/unauthorized';
  return <Navigate to={home} replace />;
}

function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[300px]">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
    </div>
  );
}

function App() {
  const {
    isAuthenticated,
    isLoading,
    isInitialized,
    passwordChangeRequired,
    logout,
  } = useAuth();

  // Wait for auth to initialize before rendering routes
  if (!isInitialized || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Mounted once for the app lifetime — patches window.fetch so a 401 on
          any non-auth endpoint triggers a clean logout + redirect to /login. */}
      <SessionExpirationHandler />
      <PreviewBanner />
      <Routes>
      {/* Public Routes */}
      <Route path="/login" element={
        isAuthenticated ? (
          passwordChangeRequired ? (
            <Navigate to="/change-password" replace />
          ) : (
            <Navigate to="/dashboard" replace />
          )
        ) : (
          <LoginPage />
        )
      } />
      {/* Public signup removed — users are now admin-invited via
          /api/admin/users. Any old link to /signup routes to /login. */}
      <Route path="/signup" element={<Navigate to="/login" replace />} />
      {/* Forced password-change flow for users on an admin-issued temp
          password. The http-client interceptor redirects here on 403
          PASSWORD_CHANGE_REQUIRED; deliberately standalone (no Layout)
          so a pending user can't navigate away. Allowed for both auth'd
          and not-yet-auth'd — an unauthenticated visit just fails the
          form's API call and surfaces the error. */}
      <Route path="/change-password" element={<ChangePasswordPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/unauthorized" element={<UnauthorizedPage />} />

      {/* Protected Routes with Layout (sidebar + header).
          Guarded by passwordChangeRequired too — users on a temp password
          get force-routed to /change-password before Layout + dashboard
          ever render. This replaces the reactive "first API call gets
          403, http-client redirects" flow that caused a brief dashboard
          flash before the redirect. */}
      <Route path="/" element={
        isAuthenticated ? (
          passwordChangeRequired ? (
            <Navigate to="/change-password" replace />
          ) : (
            <Layout onLogout={logout} />
          )
        ) : (
          <Navigate to="/login" replace />
        )
      }>
        <Route index element={<Navigate to="/dashboard" replace />} />
        {/*
          Routes are wrapped in <FeatureGate featureKey="..."> so a
          direct URL hit by a user whose role lacks the feature is
          redirected (default → /dashboard). FeatureGate consults
          `useFeatureAccess`, which has a bootstrap fallback so users
          aren't bounced before the matrix seeder has run for their
          role. Pages without a FeatureGate are intentionally open
          to any authenticated user (dashboard sub-tiles, /home,
          loading/debug demos, the property detail/edit sub-routes
          which inherit gating from /properties).
        */}
        <Route path="dashboard" element={<DashboardRoute><Suspense fallback={<PageLoader />}><DashboardPage /></Suspense></DashboardRoute>} />
        <Route path="home" element={<Suspense fallback={<PageLoader />}><HomePage /></Suspense>} />
        {/* M73: any signed-in user edits their own name, work phone, home address and personal phones. */}
        <Route path="my-profile" element={<Suspense fallback={<PageLoader />}><MyProfilePage /></Suspense>} />
        <Route path="users" element={<FeatureGate featureKey="manage_users"><Suspense fallback={<PageLoader />}><ManageUsersPage /></Suspense></FeatureGate>} />
        <Route path="directory" element={<FeatureGate featureKey="hr_directory"><Suspense fallback={<PageLoader />}><DirectoryPage /></Suspense></FeatureGate>} />
        <Route path="properties" element={<FeatureGate featureKey="properties"><Suspense fallback={<PageLoader />}><PropertiesPage /></Suspense></FeatureGate>} />
        <Route path="properties/new" element={<FeatureGate featureKey="properties"><FeatureGate featureKey="properties_create"><Suspense fallback={<PageLoader />}><PropertyFormPage /></Suspense></FeatureGate></FeatureGate>} />
        <Route path="properties/:id" element={<FeatureGate featureKey="properties"><Suspense fallback={<PageLoader />}><PropertyDetailPage /></Suspense></FeatureGate>} />
        {/* /properties/:id/history is a deep-link alias for the same detail
            page; PropertyDetailPage detects the trailing `/history` and
            auto-scrolls to the History section on mount (F-021). */}
        <Route path="properties/:id/history" element={<FeatureGate featureKey="properties"><Suspense fallback={<PageLoader />}><PropertyDetailPage /></Suspense></FeatureGate>} />
        <Route path="properties/:id/edit" element={<FeatureGate featureKey="properties"><FeatureGate featureKey="properties_edit"><Suspense fallback={<PageLoader />}><PropertyFormPage /></Suspense></FeatureGate></FeatureGate>} />
        <Route path="ingestion" element={<FeatureGate featureKey="data_ingestion"><Suspense fallback={<PageLoader />}><IngestionPage /></Suspense></FeatureGate>} />
        <Route path="ingestion/review/:jobId" element={<FeatureGate featureKey="data_ingestion"><Suspense fallback={<PageLoader />}><IngestionReviewPage /></Suspense></FeatureGate>} />
        <Route path="ingestion/jobs/:id" element={<FeatureGate featureKey="data_ingestion"><Suspense fallback={<PageLoader />}><IngestionJobDetailPage /></Suspense></FeatureGate>} />
        <Route path="ingestion/photos" element={<FeatureGate featureKey="data_ingestion"><Suspense fallback={<PageLoader />}><IngestionPhotosPage /></Suspense></FeatureGate>} />
        {/* /ingestion/spreadsheet was the legacy per-file-type page; the
            unified upload on /ingestion now auto-detects CSV/XLSX/PDF/.eml.
            Keep the redirect so old bookmarks land on the working flow. */}
        <Route path="ingestion/spreadsheet" element={<Navigate to="/ingestion" replace />} />
        {/* /scraping was an early route name before the unified "Data Ingestion"
            surface landed; researchers still type it from old bookmarks. Redirect
            to the canonical entry point so they don't silently end up on the
            dashboard via the catch-all + /login bounce. */}
        <Route path="scraping" element={<Navigate to="/ingestion" replace />} />
        {/* /transactions tombstoned (R26): merged into /comps?tab=transactions.
            Permanent redirect preserves bookmarks and external links. */}
        <Route path="transactions" element={<Navigate to="/comps?tab=transactions" replace />} />
        {/* Full-page transaction details — reached from the transaction
            side panel's "Open full details" link. Supports both lease
            and sale via the `:kind` param so each side has its own URL
            for sharing / deep-linking. Inherits the comp_intelligence
            feature gate from /comps since transactions live under that
            surface. */}
        <Route path="transactions/:kind/:id" element={<FeatureGate featureKey="comp_intelligence"><Suspense fallback={<PageLoader />}><TransactionDetailPage /></Suspense></FeatureGate>} />
        <Route path="deals" element={<FeatureGate featureKey="deals"><Suspense fallback={<PageLoader />}><DealsPage /></Suspense></FeatureGate>} />
        <Route path="comps" element={<FeatureGate featureKey="comp_intelligence"><Suspense fallback={<PageLoader />}><CompIntelligencePage /></Suspense></FeatureGate>} />
        {/* Comp workflow (delegated + self-serve paths) */}
        {/* TODO: re-enable Self Serve Search — disabled 2026-05-15 */}
        {/* <Route path="comps/search" element={<FeatureGate featureKey="comp_intelligence"><Suspense fallback={<PageLoader />}><CompSearchPage /></Suspense></FeatureGate>} /> */}
        <Route path="comps/requests" element={<FeatureGate featureKey="comp_requests"><Suspense fallback={<PageLoader />}><MyRequestsPage /></Suspense></FeatureGate>} />
        <Route path="comps/reports" element={<FeatureGate featureKey="comp_reports"><Suspense fallback={<PageLoader />}><AllReportsPage /></Suspense></FeatureGate>} />
        {/* F-020 — Read-only catalog of the 6 code-defined report layouts.
            Lets researchers preview each variant from sample data before
            picking one in the Report Builder. Gated on comp_intelligence
            (same as /comps) because it's discoverable from that surface. */}
        <Route path="comps/templates" element={<FeatureGate featureKey="comp_intelligence"><Suspense fallback={<PageLoader />}><CompReportTemplatesPage /></Suspense></FeatureGate>} />
        <Route path="comps/requests/new" element={<FeatureGate featureKey="comp_requests"><Suspense fallback={<PageLoader />}><NewCompRequestPage /></Suspense></FeatureGate>} />
        <Route path="comps/requests/:id" element={<FeatureGate featureKey="comp_requests"><Suspense fallback={<PageLoader />}><CompRequestDetailPage /></Suspense></FeatureGate>} />
        <Route path="comps/requests/:id/report" element={<FeatureGate featureKey="comp_reports"><Suspense fallback={<PageLoader />}><CompReportPage /></Suspense></FeatureGate>} />
        <Route path="comps/requests/:id/report/preview" element={<FeatureGate featureKey="comp_reports"><Suspense fallback={<PageLoader />}><CompReportPreviewPage /></Suspense></FeatureGate>} />
        {/* /comps/queue tombstoned (2026-07-08): the standalone Research
            Queue page was retired and its content now renders as a
            permission-gated widget on /dashboard for the Research +
            Manager roles. The redirect preserves old bookmarks. */}
        <Route path="comps/queue" element={<Navigate to="/dashboard" replace />} />
        {/* F-TT (2026-06-19): broker submission form (any signed-in
            user can post) + research queue (gated to research-tier
            via comp_research_queue, same as the comp request queue). */}
        <Route path="submit-to-research" element={<Suspense fallback={<PageLoader />}><SubmitToResearchPage /></Suspense>} />
        <Route path="reviews/broker-submissions" element={<FeatureGate featureKey="comp_research_queue"><Suspense fallback={<PageLoader />}><BrokerSubmissionsQueuePage /></Suspense></FeatureGate>} />
        <Route path="ops" element={<FeatureGate featureKey="ops_dashboard"><Suspense fallback={<PageLoader />}><OpsDashboardPage /></Suspense></FeatureGate>} />
        <Route path="verification" element={<FeatureGate featureKey="verification"><Suspense fallback={<PageLoader />}><VerificationPage /></Suspense></FeatureGate>} />
        {/* Internal dev/demo playground — never for the client. Whitelist-gated like the other devtools routes. */}
        <Route path="loading-test" element={<SuperAdminRoute><Suspense fallback={<PageLoader />}><LoadingTestPage /></Suspense></SuperAdminRoute>} />
        {/* Internal dev/demo playground — never for the client. Whitelist-gated like the other devtools routes. */}
        <Route path="number-formatting" element={<SuperAdminRoute><Suspense fallback={<PageLoader />}><NumberFormattingDemo /></Suspense></SuperAdminRoute>} />
        <Route path="settings" element={<SettingsHubRoute><Suspense fallback={<PageLoader />}><SettingsPage /></Suspense></SettingsHubRoute>} />
        <Route path="settings/permissions" element={<FeatureGate featureKey="settings_permissions" fallback={<SettingsDenied title="Security & Permissions" permission="settings_permissions" />}><Suspense fallback={<PageLoader />}><PermissionMatrixSettings /></Suspense></FeatureGate>} />
        <Route path="settings/dashboard-designer" element={<FeatureGate featureKey="settings_permissions" fallback={<SettingsDenied title="Dashboard designer" permission="settings_permissions" crumbs={[{ label: 'Security & Permissions', to: '/settings/permissions' }]} />}><Suspense fallback={<PageLoader />}><DashboardDesignerPage /></Suspense></FeatureGate>} />
        {/* /settings/suggestions was the standalone field-change-suggestions
            page; retired 2026-06-21 as part of the Plan A consolidation.
            The merged review surface lives at /reviews/submissions. */}
        <Route path="settings/suggestions" element={<Navigate to="/reviews/submissions" replace />} />
        <Route path="settings/submarkets" element={<FeatureGate featureKey="settings_submarkets" fallback={<SettingsDenied title="Submarket Boundaries" permission="settings_submarkets" />}><Suspense fallback={<PageLoader />}><SubmarketSettingsPage /></Suspense></FeatureGate>} />
        <Route path="settings/data-maintenance" element={<DataMaintenanceRoute><Suspense fallback={<PageLoader />}><DataMaintenanceSettingsPage /></Suspense></DataMaintenanceRoute>} />
        <Route path="settings/general" element={<AppSettingsRoute><Suspense fallback={<PageLoader />}><AppSettingsPage /></Suspense></AppSettingsRoute>} />
        <Route path="settings/lease-terms" element={<LeaseTermsRoute><Suspense fallback={<PageLoader />}><LeaseTermsSettingsPage /></Suspense></LeaseTermsRoute>} />
        <Route path="settings/forms" element={<FormsSettingsRoute><Suspense fallback={<PageLoader />}><FormTypesSettingsPage /></Suspense></FormsSettingsRoute>} />
        {/* M286 idea 17: AI Model Settings, AI Lab and AI Spend are the tabs of Settings → AI, with the ai.* settings groups
            (Caps & corrections); the old URLs redirect to their tab, the lab's run and variant pages keep theirs. */}
        <Route path="settings/ai" element={<AiAreaRoute fallback={<SettingsDenied title="AI" permission="ai_lab" />}><Suspense fallback={<PageLoader />}><AiPage /></Suspense></AiAreaRoute>} />
        <Route path="settings/ai-models" element={<Navigate to="/settings/ai" replace />} />
        <Route path="settings/ai-lab" element={<Navigate to="/settings/ai?tab=lab" replace />} />
        <Route path="settings/ai-spend" element={<Navigate to="/settings/ai?tab=spend" replace />} />
        <Route path="settings/ai-lab/runs/:id" element={<AiAreaRoute fallback={<SettingsDenied title="AI Lab run" permission="ai_lab" crumbs={AI_LAB_CRUMBS} />}><Suspense fallback={<PageLoader />}><AiLabRunPage /></Suspense></AiAreaRoute>} />
        <Route path="settings/ai-lab/variants/:id" element={<AiAreaRoute fallback={<SettingsDenied title="Prompt variant" permission="ai_lab" crumbs={AI_LAB_CRUMBS} />}><Suspense fallback={<PageLoader />}><AiLabVariantPage /></Suspense></AiAreaRoute>} />
        {/* M286: Reference Data, Property Types and Property-Type Fields are the tabs of Data curation; each tab keeps
            its gate (useDataCurationTabs) and the old URLs redirect to it (DataCurationRedirect). */}
        <Route path="settings/data-curation" element={<Suspense fallback={<PageLoader />}><DataCurationPage /></Suspense>} />
        <Route path="settings/reference-data" element={<DataCurationRedirect tab="lookups" />} />
        <Route path="settings/property-types-uses" element={<DataCurationRedirect tab="types" />} />
        <Route path="settings/property-type-fields" element={<DataCurationRedirect tab="fields" />} />
        <Route path="settings/sources" element={<FeatureGate featureKey="settings_data_sources" fallback={<SettingsDenied title="Data Sources" permission="settings_data_sources" />}><Suspense fallback={<PageLoader />}><SourcesSettingsPage /></Suspense></FeatureGate>} />
        {/* M286: Admin Info and Calculated Fields moved to /devtools (AdminOrAigRoute). */}
        <Route path="settings/admin-info" element={<Navigate to="/devtools/admin-info" replace />} />
        <Route path="settings/calculated-fields" element={<Navigate to="/devtools/calculated-fields" replace />} />
        <Route path="settings/listing-templates" element={<ListingTemplatesRoute><Suspense fallback={<PageLoader />}><ListingTemplatesSettingsPage /></Suspense></ListingTemplatesRoute>} />
        {/* M286: Widget Permissions is a tab of Security & Permissions. */}
        <Route path="settings/widget-permissions" element={<Navigate to="/settings/permissions?tab=widgets" replace />} />
        <Route path="reviews" element={<FeatureGate featureKey="reviews"><Suspense fallback={<PageLoader />}><ReviewQueuePage /></Suspense></FeatureGate>} />
        <Route path="reviews/submissions" element={<FeatureGate featureKey="reviews"><Suspense fallback={<PageLoader />}><SubmissionReviewQueuePage /></Suspense></FeatureGate>} />
        <Route path="reviews/:id" element={<FeatureGate featureKey="reviews"><Suspense fallback={<PageLoader />}><ReviewDetailPage /></Suspense></FeatureGate>} />
        <Route path="documents" element={<FeatureGate featureKey="documents"><Suspense fallback={<PageLoader />}><DocumentsPage /></Suspense></FeatureGate>} />
        <Route path="request-tracker" element={<RequestTrackerRoute><Suspense fallback={<PageLoader />}><RequestTrackerPage /></Suspense></RequestTrackerRoute>} />
        <Route path="forms" element={<Suspense fallback={<PageLoader />}><FormsInboxPage /></Suspense>} />
        <Route path="forms/new" element={<Suspense fallback={<PageLoader />}><NewFormPage /></Suspense>} />
        <Route path="forms/start" element={<Suspense fallback={<PageLoader />}><FormsDirectoryPage /></Suspense>} />
        <Route path="forms/:id" element={<Suspense fallback={<PageLoader />}><FormSubmissionPage /></Suspense>} />
        <Route path="devtools/release-notes" element={<SuperAdminRoute><Suspense fallback={<PageLoader />}><ReleaseNotesPage /></Suspense></SuperAdminRoute>} />
        <Route path="devtools/error-reports" element={<SuperAdminRoute><Suspense fallback={<PageLoader />}><DeveloperErrorReportsPage /></Suspense></SuperAdminRoute>} />
        {/* Admin Info — internal references for the admin team (e.g. test broker credentials); Calculated Fields (F-T,
            2026-06-15) — the read-only viewer of every derived value. An admin role or AIG staff (M286). */}
        <Route path="devtools/admin-info" element={<AdminOrAigRoute title="Admin Info"><Suspense fallback={<PageLoader />}><AdminInfoSettingsPage /></Suspense></AdminOrAigRoute>} />
        <Route path="devtools/calculated-fields" element={<AdminOrAigRoute title="Calculated Fields"><Suspense fallback={<PageLoader />}><CalculatedFieldsSettingsPage /></Suspense></AdminOrAigRoute>} />
        {/* Listings — feature:listings (Brokers: their own agreements, read-only),
            listings_work or an admin (ListingsRoute). The Lease intelligence
            preview moved to /property-management (L1-3). */}
        <Route path="listings" element={<ListingsRoute><Suspense fallback={<PageLoader />}><ListingsLayout /></Suspense></ListingsRoute>}>
          <Route index element={<ListingProjectsPage />} />
          <Route path="work" element={<ListingsRoute work><ListingsDashboardPage /></ListingsRoute>} />
          <Route path="workload" element={<ListingsRoute workload><TeamWorkloadPage /></ListingsRoute>} />
          <Route path="signs" element={<SignRequestsPage />} />
          <Route path="new" element={<ListingsRoute work><NewListingPage /></ListingsRoute>} />
          <Route path="leases" element={<Navigate to="/property-management/leases" replace />} />
          <Route path=":agreementId" element={<ListingProjectPage />} />
          <Route path=":agreementId/activity" element={<ListingActivityPage />} />
        </Route>
        {/* Property management (PM-1) — the feature:property_management keys or an admin
            (PropertyManagementRoute → /unauthorized). Index: the real managed-properties list;
            leases: the Lease intelligence preview (sample data) in its own layout, unchanged;
            expirations: managed leases by expiration month (M42). */}
        <Route path="property-management" element={<PropertyManagementRoute><Suspense fallback={<PageLoader />}><PropertyManagementSection /></Suspense></PropertyManagementRoute>}>
          <Route index element={<ManagedPropertiesPage />} />
          <Route path="expirations" element={<LeaseExpirationsPage />} />
          <Route element={<PropertyManagementLayout />}>
            <Route path="leases" element={<LeaseIntelligencePage />} />
          </Route>
        </Route>
      </Route>

      {/* Catch-all: redirect to login */}
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
    </>
  );
}

export default App;